import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { ReservationsActions } from 'state/modules/reservations';
import moment from 'moment';
import { DateTimePickerField, CheckboxField } from 'components/FormFields';

import { BaseModal } from 'components/UI';

const ChangeTimesReservationModal = ({ id, reservation, timezone, open, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { fetchedReservationSuccess, reservationLoading, errors, fetchedReservationError } =
    useSelector((state) => state.reservations);
  const [showBypassRules, setShowBypassRules] = useState(false);

  // Validation schema of update user form
  const UpdateTimesSchema = Yup.object().shape({
    end_reserved: Yup.string().required(i18n.t('common.required_field')).nullable(),
    start_reserved: Yup.string().required(i18n.t('common.required_field')).nullable(),
  });

  // Initials values of the form
  const initialValues = {
    end_reserved: reservation?.end_reserved || moment().add(3, 'hour'),
    start_reserved: reservation?.start_reserved || moment().add(1, 'hour'),
    options: {},
  };

  useEffect(() => {
    if (fetchedReservationSuccess) {
      handleClick();
      dispatch(ReservationsActions.resetUpdateReservation());
    }
  }, [fetchedReservationSuccess, handleClick, dispatch]);

  // Bypass field should be shown once a error is present.
  useEffect(() => {
    if (fetchedReservationError) {
      setShowBypassRules(true);
    }
  }, [fetchedReservationError]);

  // Bypass field should be hidden on initial render / Closed - Reopened
  useEffect(() => {
    setShowBypassRules(false);
  }, []);

  /**
   * Handle form submit
   * @param {object} values Object with the value of fields of the form
   */
  const onSubmit = (values) => {
    if (id) {
      const payload = { ...values };
      payload.action = 'ALTERTIMES';
      return dispatch(ReservationsActions.updateReservation(id, payload));
    }
    return dispatch(ReservationsActions.createReservation(values));
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      loading={reservationLoading}
      title={i18n.t('reservations.update_times')}
      enterButtonLabel={i18n.t('reservations.update_times')}
      privileges={{ action: 'update_times', subject: 'reservation' }}
    >
      <Grid
        container
        direction="column"
        data-cy="change-times-reservation-modal"
        alignItems="flex-start"
      >
        <Box width="100%">
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={UpdateTimesSchema}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values, dirty, setFieldValue }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {reservation.status !== 'INPROGRESS' && (
                    <Grid item xs={12}>
                      <Box>
                        <Field
                          utc
                          name="start_reserved"
                          component={DateTimePickerField}
                          inputProps={{ 'data-cy': 'start-reserved-picker' }}
                          label={i18n.t('reservations.start_date')}
                          asyncErrors={errors?.start_time}
                          editable={true}
                          timezone={timezone}
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        utc
                        name="end_reserved"
                        component={DateTimePickerField}
                        inputProps={{ 'data-cy': 'end-reserved-picker' }}
                        label={i18n.t('reservations.end_date')}
                        asyncErrors={errors?.end_time}
                        editable={true}
                        timezone={timezone}
                      />
                    </Box>
                  </Grid>
                  {showBypassRules && (
                    <Grid item xs={12}>
                      <Box>
                        <Field
                          name="bypass"
                          labelPlacement="end"
                          field={{
                            name: 'options.bypass_rules',
                            value: values?.options?.bypass_rules,
                          }}
                          component={CheckboxField}
                          label={i18n.t('reservations.bypass')}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </BaseModal>
  );
};

ChangeTimesReservationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  reservation: PropTypes.any.isRequired,
  handleClick: PropTypes.func.isRequired,
};

ChangeTimesReservationModal.defaultProps = {
  open: false,
  reservation: null,
  handleClick: () => {},
};

export default ChangeTimesReservationModal;
